// Pure helpers for the orderSpy History comparison grid (brief decision 6).
//
// Kept dependency-free so the cell-selection rules (a scheduled capture beats a
// later manual/fetch one; a failed capture is a marked cell; a day with no
// capture is a missing cell) and the row-sort rules (`sortHistoryRows`: any
// column, missing/failed date cells sink to the bottom) are unit-tested directly.

import type { SortDir, WaitlistOrder } from "@/utils/orderSpySort";

/** One saved capture, as read back from public.pos_waitlist_snapshots. */
export interface WaitlistSnapshot {
  id: number | string;
  store_id: string;
  business_date: string;
  source: string;
  captured_at: string;
  /** null when that capture's POS fetch failed. */
  order_count: number | null;
  earliest_promise_time: string | null;
  latest_promise_time: string | null;
  error: string | null;
  /** Loaded on demand (cell drill-down); the grid query omits it. */
  orders?: WaitlistOrder[];
}

/** The chosen capture for one store on one business date, plus how many were
 *  taken that day (so the UI can reveal the alternates). */
export interface HistoryCell {
  snapshot: WaitlistSnapshot;
  orderCount: number | null;
  error: string | null;
  /** true when the chosen capture failed (order_count is null / error set). */
  failed: boolean;
  /** total captures for this store+date, including the ones not shown. */
  captureCount: number;
}

export interface HistoryRow {
  storeId: string;
  /** One entry per business date, aligned to the dates passed in; null = the
   *  store had no capture that day (rendered as the em-dash placeholder). */
  cells: (HistoryCell | null)[];
  /** Sum of order_count across the range; failed and missing days count 0. */
  total: number;
}

export interface HistoryGrid {
  rows: HistoryRow[];
  /** Column totals, aligned to the business dates. */
  dateTotals: number[];
  grandTotal: number;
}

/** Groups snapshots by `${store_id} ${business_date}` for O(1) cell lookup. */
function cellKey(storeId: string, businessDate: string): string {
  return `${storeId} ${businessDate}`;
}

/**
 * Picks the capture to display for one store+date from all of that day's
 * captures: the `scheduled` one if present (the canonical end-of-day reading),
 * otherwise the latest by captured_at. Returns null for an empty list.
 */
export function selectSnapshot(candidates: readonly WaitlistSnapshot[]): WaitlistSnapshot | null {
  if (candidates.length === 0) return null;
  const scheduled = candidates.filter((s) => s.source === "scheduled");
  const pool = scheduled.length > 0 ? scheduled : candidates;
  // Latest wins within the chosen pool (multiple scheduled rows are unusual but
  // possible after a manual re-run of the nightly job).
  return pool.reduce((latest, s) =>
    new Date(s.captured_at).getTime() >= new Date(latest.captured_at).getTime() ? s : latest,
  );
}

/**
 * The ordered list of store rows for the History grid: every currently-pinned
 * store (in the given order) followed by any store that only appears in the
 * snapshot history (no longer pinned but still worth comparing), sorted for a
 * stable display.
 */
export function historyStoreIds(
  pinnedStoreIds: readonly string[],
  snapshots: readonly WaitlistSnapshot[],
): string[] {
  const seen = new Set<string>();
  const ordered: string[] = [];
  for (const id of pinnedStoreIds) {
    if (id && !seen.has(id)) {
      seen.add(id);
      ordered.push(id);
    }
  }
  const extras = new Set<string>();
  for (const s of snapshots) {
    if (s.store_id && !seen.has(s.store_id)) extras.add(s.store_id);
  }
  return [...ordered, ...[...extras].sort((a, b) => a.localeCompare(b))];
}

/**
 * Builds the store × business-date comparison grid. Each cell shows the selected
 * capture (scheduled beats latest); a failed capture is marked; a day with no
 * capture is null (a missing cell). Row and column totals sum order_count,
 * counting failed and missing days as zero.
 */
export function buildHistoryGrid(
  snapshots: readonly WaitlistSnapshot[],
  storeIds: readonly string[],
  businessDates: readonly string[],
): HistoryGrid {
  // Bucket every capture by store+date.
  const buckets = new Map<string, WaitlistSnapshot[]>();
  for (const s of snapshots) {
    const key = cellKey(s.store_id, s.business_date);
    const list = buckets.get(key);
    if (list) list.push(s);
    else buckets.set(key, [s]);
  }

  const dateTotals = businessDates.map(() => 0);
  const rows: HistoryRow[] = storeIds.map((storeId) => {
    let rowTotal = 0;
    const cells = businessDates.map((date, colIndex): HistoryCell | null => {
      const candidates = buckets.get(cellKey(storeId, date));
      if (!candidates || candidates.length === 0) return null;
      const chosen = selectSnapshot(candidates)!;
      const failed = chosen.order_count == null || chosen.error != null;
      const count = failed ? 0 : chosen.order_count ?? 0;
      rowTotal += count;
      dateTotals[colIndex] += count;
      return {
        snapshot: chosen,
        orderCount: chosen.order_count,
        error: chosen.error,
        failed,
        captureCount: candidates.length,
      };
    });
    return { storeId, cells, total: rowTotal };
  });

  const grandTotal = dateTotals.reduce((sum, n) => sum + n, 0);
  return { rows, dateTotals, grandTotal };
}

/** Which comparison-table column to sort by: the store label, the Total
 *  column, or one date column (keyed by its "YYYY-MM-DD" business date, so the
 *  key stays meaningful when the visible range changes). */
export type HistorySortKey =
  | { column: "store" }
  | { column: "total" }
  | { column: "date"; date: string };

export interface HistorySort {
  key: HistorySortKey;
  dir: SortDir;
}

/** Busiest store first — the sort the History table opens with and falls back
 *  to when the sorted date column is no longer in the visible range. */
export const DEFAULT_HISTORY_SORT: HistorySort = { key: { column: "total" }, dir: "desc" };

export function sortKeysEqual(a: HistorySortKey, b: HistorySortKey): boolean {
  if (a.column !== b.column) return false;
  return a.column === "date" && b.column === "date" ? a.date === b.date : true;
}

/**
 * The sort that actually applies for the given visible dates: a date-column
 * sort whose date is not among `businessDates` (the range changed since it was
 * chosen) resolves to `DEFAULT_HISTORY_SORT`; anything else is returned as-is.
 * The table's header indicators and its row order both derive from this, so
 * the active column is always visible and clickable.
 */
export function resolveHistorySort(sort: HistorySort, businessDates: readonly string[]): HistorySort {
  if (sort.key.column === "date" && !businessDates.includes(sort.key.date)) return DEFAULT_HISTORY_SORT;
  return sort;
}

/**
 * Sorts the comparison table's store rows by the given column. A missing or
 * failed cell in a date column sinks to the bottom regardless of direction
 * (same blanks-sink rule as `compareOrders`). `businessDates` must be the
 * same list the rows' cells were built against. Returns a new array; `rows`
 * is never mutated.
 */
export function sortHistoryRows(
  rows: readonly HistoryRow[],
  sort: HistorySort,
  businessDates: readonly string[],
  labelFor: (storeId: string) => string = (id) => id,
): HistoryRow[] {
  const { key, dir } = resolveHistorySort(sort, businessDates);
  const dateIndex = key.column === "date" ? businessDates.indexOf(key.date) : -1;

  const value = (row: HistoryRow): number | string | null => {
    if (key.column === "store") return labelFor(row.storeId).toLowerCase();
    if (key.column === "total") return row.total;
    const cell = row.cells[dateIndex];
    if (!cell || cell.failed) return null;
    return cell.orderCount ?? 0;
  };

  return [...rows].sort((a, b) => {
    const av = value(a);
    const bv = value(b);
    // Blanks (missing/failed date cells) always sink to the bottom.
    if (av === null && bv === null) return 0;
    if (av === null) return 1;
    if (bv === null) return -1;

    let cmp: number;
    if (typeof av === "string" && typeof bv === "string") {
      cmp = av.localeCompare(bv, undefined, { numeric: true, sensitivity: "base" });
    } else {
      cmp = av < bv ? -1 : av > bv ? 1 : 0;
    }
    return dir === "asc" ? cmp : -cmp;
  });
}

/** The "YYYY-MM-DD" calendar date of `now` in Los Angeles — the same rule the
 *  snapshot writer uses for business_date, so the grid's columns line up with
 *  the rows regardless of the operator's browser timezone. */
export function todayInLA(now: Date = new Date()): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Los_Angeles",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(now);
}

/** Shifts a "YYYY-MM-DD" date by `days` (may be negative) in pure calendar
 *  arithmetic (UTC-anchored, so no DST or local-zone drift). */
export function shiftIsoDate(isoDate: string, days: number): string {
  const [y, m, d] = isoDate.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}

/** Every "YYYY-MM-DD" date from `fromIso` to `toIso` inclusive. */
export function isoDateRange(fromIso: string, toIso: string): string[] {
  const dates: string[] = [];
  for (let d = fromIso; d <= toIso; d = shiftIsoDate(d, 1)) dates.push(d);
  return dates;
}

/** The last `days` LA business dates ending today, as [from, to]. */
export function presetIsoRange(days: number, now: Date = new Date()): { from: string; to: string } {
  const to = todayInLA(now);
  return { from: shiftIsoDate(to, -(days - 1)), to };
}

/** Longest span (inclusive days) the custom History picker allows. */
export const MAX_HISTORY_SPAN_DAYS = 92;

/** Clamps [fromIso, toIso] to at most `maxDays` inclusive, keeping the end the
 *  operator just picked (`anchor`) and pulling the other end in toward it. */
export function clampIsoRange(
  fromIso: string,
  toIso: string,
  anchor: "from" | "to",
  maxDays: number = MAX_HISTORY_SPAN_DAYS,
): { from: string; to: string } {
  const span = isoDateRange(fromIso, toIso).length;
  if (span <= maxDays) return { from: fromIso, to: toIso };
  return anchor === "to"
    ? { from: shiftIsoDate(toIso, -(maxDays - 1)), to: toIso }
    : { from: fromIso, to: shiftIsoDate(fromIso, maxDays - 1) };
}

export interface ChartSeriesStore {
  storeId: string;
  location: string;
  brand: string;
}

export interface ChartSeriesLabel {
  legend: string;
  tooltip: string;
}

/**
 * Chart series labels for the selected stores. The legend shows the location
 * text alone (the brand is carried by the line colour) unless two selected
 * stores from different brands share a location, in which case both get
 * " (Brand)" appended so they are distinguishable in text. The tooltip always
 * reads "Location · Brand".
 */
export function chartSeriesLabels(
  stores: readonly ChartSeriesStore[],
): Map<string, ChartSeriesLabel> {
  const brandsByLocation = new Map<string, Set<string>>();
  for (const s of stores) {
    const set = brandsByLocation.get(s.location) ?? new Set<string>();
    set.add(s.brand);
    brandsByLocation.set(s.location, set);
  }
  const out = new Map<string, ChartSeriesLabel>();
  for (const s of stores) {
    const collides = (brandsByLocation.get(s.location)?.size ?? 0) > 1 && s.brand !== "";
    out.set(s.storeId, {
      legend: collides ? `${s.location} (${s.brand})` : s.location,
      tooltip: s.brand ? `${s.location} · ${s.brand}` : s.location,
    });
  }
  return out;
}
